import React, { useEffect } from 'react';
import {
  ReactFlow,
  Controls,
  Background,
  type Node,
  type Edge,
  MiniMap,
  ControlButton,
  useReactFlow,
  ReactFlowProvider
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { FlowNode } from './nodes/flow-node';
import { ActionNode } from './nodes/action-node';
import { StartNode } from './nodes/start-node';
import { EndNode } from './nodes/end-node';
import { FlowBlockNode } from './nodes/flow-block-node';
import { FlowGroupNode } from './nodes/flow-group-node';
import { OutcomeEdge } from './edges/outcome-edge';
import { MapPinIcon, MapPinOffIcon } from 'lucide-react';
import { useLocalStorage } from '@@/hooks/use-local-storage';
import { useLogNavigation } from '../../contexts/log-navigation-context';
import { LogType } from '../../types/visualizer';
import { ProcessorFactory } from '../../services/processor-factory';
import { useGetRealTimeLogs } from '../../hooks/use-get-realtime-logs';
import { Spinner } from '@@/ui/components';

const FlowCanvasInner = (props: { selectedLogType: LogType; nodes: Node[] }) => {
  const { currentNodeTarget, clearNodeTarget } = useLogNavigation();
  const reactFlowInstance = useReactFlow();

  useEffect(() => {
    if (!currentNodeTarget) {
      return;
    }

    const targetNode = props.nodes[currentNodeTarget.index];

    if (targetNode) {
      reactFlowInstance.fitView({
        padding: 0.5,
        duration: 500,
        nodes: [targetNode],
        interpolate: 'linear'
      });

      clearNodeTarget();
    }
  }, [currentNodeTarget]);

  return null;
};

export const FlowCanvas = (props: {
  contactId: string;
  initiationTimestamp: string;
  disconnectTimestamp: string | null;
  selectedLogType: LogType;
}) => {
  const { data: contactFlowLogs } = useGetRealTimeLogs<'CONTACT_FLOW'>({
    contactId: props.contactId,
    kind: 'CONTACT_FLOW',
    startAt: props.initiationTimestamp,
    endAt: props.disconnectTimestamp ?? null
  });

  const { data: interactionLogs } = useGetRealTimeLogs<'INTERACTION'>({
    contactId: props.contactId,
    kind: 'INTERACTION',
    date: props.initiationTimestamp.split('T')[0]
  });
  const [showMinimap, setShowMinimap] = useLocalStorage<boolean>('cv-show-mini-map', true);
  const { navigateToLog } = useLogNavigation();

  function onNodeClick(_event: React.MouseEvent, node: Node) {
    if (props.selectedLogType !== LogType.FLOW_LOG) return;
    if (!node.data.identifier || typeof node.data.executionCount !== 'number') return;

    const nodeIdentifier = node.data.identifier;
    const targetExecutionCount = node.data.executionCount;

    let currentExecutionCount = 0;
    if (!contactFlowLogs) return;

    const logIndex = contactFlowLogs.events.findIndex((event) => {
      if (event.Identifier === nodeIdentifier) {
        if (currentExecutionCount === targetExecutionCount) {
          return true;
        }
        currentExecutionCount++;
      }
      return false;
    });

    if (logIndex >= 0) {
      navigateToLog({
        type: LogType.FLOW_LOG,
        index: logIndex
      });
    }
  }

  let nodeData: {
    nodes: Node[];
    edges: Edge[];
  } = {
    nodes: [],
    edges: []
  };

  if (props.selectedLogType === LogType.FLOW_LOG) {
    if (!contactFlowLogs?.events) {
      return (
        <div className='flex items-center justify-center h-full text-neutral-500'>
          <Spinner />
        </div>
      );
    }

    const processor = ProcessorFactory.getProcessor(LogType.FLOW_LOG);
    nodeData = processor.processAndConvert(contactFlowLogs?.events || []);
  } else if (props.selectedLogType === LogType.INTERACTION_LOG) {
    if (!interactionLogs) {
      return (
        <div className='flex items-center justify-center h-full text-neutral-500'>
          <Spinner />
        </div>
      );
    }

    const processor = ProcessorFactory.getProcessor(LogType.INTERACTION_LOG);
    nodeData = processor.processAndConvert(interactionLogs?.object?.InteractionLogs || []);
  }

  return (
    <ReactFlowProvider>
      <ReactFlow
        defaultNodes={nodeData.nodes}
        defaultEdges={nodeData.edges}
        nodeTypes={{
          flow: FlowNode,
          action: ActionNode,
          start: StartNode,
          end: EndNode,
          flowBlock: FlowBlockNode,
          flowGroup: FlowGroupNode
        }}
        edgeTypes={{
          outcome: OutcomeEdge
        }}
        onlyRenderVisibleElements={true}
        nodesDraggable={true}
        nodesConnectable={false}
        elementsSelectable={true}
        onNodeClick={onNodeClick}
        zoomOnScroll={true}
        zoomOnPinch={true}
        zoomOnDoubleClick={true}
        panOnDrag={true}
        panOnScroll={false}
        defaultEdgeOptions={{
          zIndex: 1000
        }}
      >
        <Background gap={16} />
        <FlowCanvasInner selectedLogType={props.selectedLogType} nodes={nodeData.nodes} />
        <Controls showInteractive={false}>
          <ControlButton
            onClick={() => setShowMinimap(!showMinimap)}
            title={showMinimap ? 'Hide Minimap' : 'Show Minimap'}
          >
            {showMinimap ? <MapPinIcon /> : <MapPinOffIcon />}
          </ControlButton>
        </Controls>
        {showMinimap && (
          <MiniMap
            pannable={true}
            zoomable={true}
            className='bg-neutral-50 border border-neutral-200 rounded-lg'
            nodeColor='rgb(178, 178, 178)'
          />
        )}
      </ReactFlow>
    </ReactFlowProvider>
  );
};
